/** What happened to one item in a batch. `error` absent = it worked. */
export interface BulkOutcome {
  id: string;
  name: string;
  error?: string;
}

/**
 * Apply `fn` to each item in turn, collecting failures instead of stopping at
 * the first one — a bulk action that dies halfway with one error message tells
 * you neither what succeeded nor why the rest didn't.
 *
 * Sequential on purpose. Each of these fans out to a delete or a duplicate that
 * touches Postgres *and* object storage, and a half-finished sequence ("the
 * first four are gone") is far easier to reason about than a half-finished
 * parallel burst.
 */
export async function runSequential<T>(
  items: T[],
  describe: (item: T) => { id: string; name: string },
  fn: (item: T) => Promise<unknown>,
  onProgress?: (done: number, total: number) => void,
): Promise<BulkOutcome[]> {
  const out: BulkOutcome[] = [];
  for (const item of items) {
    const { id, name } = describe(item);
    try {
      await fn(item);
      out.push({ id, name });
    } catch (e) {
      out.push({ id, name, error: e instanceof Error ? e.message : 'Failed' });
    }
    onProgress?.(out.length, items.length);
  }
  return out;
}

export const succeeded = (r: BulkOutcome[]) => r.filter((o) => !o.error);
export const failed = (r: BulkOutcome[]) => r.filter((o) => o.error);
